import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { exportFileSchema } from '../../../../../shared/source/data-contract/common';
import { useGlobalPermissions } from '../global-permissions';

import type { ExportFile } from '../../../../../shared/source/data-contract/common';
import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type { UseMutationResult } from '@tanstack/react-query';

export const useTalentDirectoryPermissions = (): {
    readonly canAccessTalentDirectory: boolean;
} => {
    const { data: globalPermissions } = useGlobalPermissions();

    return {
        canAccessTalentDirectory: globalPermissions?.canUserTalentDirectory ?? false,
    };
};

const useDirectoryExport = (pathname: Pathname): UseMutationResult<ExportFile> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.post(exportFileSchema, {
                pathname,
                requestOptions: {
                    timeout: 90_000,
                },
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },

        mutationKey: [pathname, 'POST'],
    });
};

export const useTalentDirectorySkillsExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/skills/export');

export const useTalentDirectoryEmployeeExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/users/export');

export const useTalentDirectoryJobExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/jobs/export');

export const useTalentDirectoryLearningOpportunityExport = (): UseMutationResult<ExportFile> =>
    useDirectoryExport('/global/directory/learning-opportunities/export');
